/**
 * Small Zustand store that collects ingest + upload progress across the app.
 *
 * Populated by two sources:
 *   1. `videoUploader.ts` as it uploads parts to S3 (Phase B).
 *   2. `commands/videoStudioCommands.ts` from `video_studio_progress` WS
 *      broadcasts emitted by the backend `VideoIngestWorker` (Phase C).
 *
 * Shared across windows so Timeline A and Timeline B both see the same queue.
 */

import { create } from 'zustand';

export interface IngestQueueItem {
  opId: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  stage?: string;
  pct?: number;
  label?: string;
  sourceEntityId?: string;
  spaceId?: string;
  error?: string;
  updatedAt: number;
}

interface IngestQueueState {
  byOpId: Record<string, IngestQueueItem>;
  upsert: (it: Partial<IngestQueueItem> & { opId: string; status: IngestQueueItem['status'] }) => void;
  remove: (opId: string) => void;
  clearSucceeded: () => void;
}

export const useIngestQueueStore = create<IngestQueueState>((set) => ({
  byOpId: {},
  upsert: (it) =>
    set((s) => ({
      byOpId: {
        ...s.byOpId,
        [it.opId]: {
          ...(s.byOpId[it.opId] || {}),
          ...it,
          updatedAt: Date.now(),
        } as IngestQueueItem,
      },
    })),
  remove: (opId) =>
    set((s) => {
      const next = { ...s.byOpId };
      delete next[opId];
      return { byOpId: next };
    }),
  clearSucceeded: () =>
    set((s) => {
      const next: Record<string, IngestQueueItem> = {};
      for (const [k, v] of Object.entries(s.byOpId)) {
        if (v.status !== 'succeeded') next[k] = v;
      }
      return { byOpId: next };
    }),
}));
